import { defineMessages, type MessageDescriptor } from 'react-intl-next';

type MessageDescriptorType = MessageDescriptor;

type CopyVariationType = {
	heading: MessageDescriptorType;
	description: MessageDescriptorType;
};

/**
 * Shared messages used across all copy variants.
 */
export const messages: {
	ctaLinkText: MessageDescriptorType;
	chatPrompt: MessageDescriptorType;
	whyAmISeeingThisReason1: MessageDescriptorType;
	whyAmISeeingThisReason2: MessageDescriptorType;
} = defineMessages({
	ctaLinkText: {
		id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.cta-link-text',
		defaultMessage: 'Try Rovo now',
		description: 'CTA link text for the non-MAU Rovo Chat CTA banner',
	},
	chatPrompt: {
		id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.chat-prompt',
		defaultMessage:
			'Hi Rovo, list three things you can do right now for me based on my recent work',
		description:
			'Auto-run prompt sent to Rovo Chat when the user clicks the CTA on the non-MAU chat CTA banner',
	},
	whyAmISeeingThisReason1: {
		id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.why-am-i-seeing-this.reason-1',
		defaultMessage: 'your existing Atlassian products',
		description: 'First reason for showing the Rovo Chat CTA to the user',
	},
	whyAmISeeingThisReason2: {
		id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.why-am-i-seeing-this.reason-2',
		defaultMessage: 'your organization having AI enabled',
		description: 'Second reason for showing the Rovo Chat CTA to the user',
	},
});

/**
 * Copy variations keyed by Switcheroo experiment cohort values.
 * Cohorts: 'v1', 'v2', 'v3' (control users never see the banner).
 */
export const COPY_VARIATIONS: {
	v1: CopyVariationType;
	v2: CopyVariationType;
	v3: CopyVariationType;
} = {
	v1: defineMessages({
		heading: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.heading.v1',
			defaultMessage: 'Unlock your AI teammate',
			description: 'Heading text for non-MAU Rovo Chat CTA banner - variant v1',
		},
		description: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.description.v1',
			defaultMessage: 'Rovo connects your tools and knowledge so work gets done for you.',
			description: 'Description text for non-MAU Rovo Chat CTA banner - variant v1',
		},
	}),
	v2: defineMessages({
		heading: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.heading.v2',
			defaultMessage: 'Knowledge that knows you',
			description: 'Heading text for non-MAU Rovo Chat CTA banner - variant v2',
		},
		description: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.description.v2',
			defaultMessage:
				'Rovo uses your Teamwork Graph to surface the right answers from your team\u2019s work and tools.',
			description: 'Description text for non-MAU Rovo Chat CTA banner - variant v2',
		},
	}),
	v3: defineMessages({
		heading: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.heading.v3',
			defaultMessage: 'Your work. On autopilot.',
			description: 'Heading text for non-MAU Rovo Chat CTA banner - variant v3',
		},
		description: {
			id: 'rovo-non-mau-chat-cta-jira-confluence-left-nav.description.v3',
			defaultMessage:
				'Rovo unifies your tools and knowledge so your AI teammate handles the busywork.',
			description: 'Description text for non-MAU Rovo Chat CTA banner - variant v3',
		},
	}),
};

/**
 * Returns the copy variation for a given cohort, falling back to v1 if unknown.
 */
export const selectCopyForCohort = (cohort: string): CopyVariationType => {
	return COPY_VARIATIONS[cohort as keyof typeof COPY_VARIATIONS] ?? COPY_VARIATIONS['v1'];
};
